<template>
  <div class="diff">
    <div class="data">
      <div
        class="title title--left lead-1"
        v-text="$t('screen.basket.existingCustomer.tabs.existing')"
      />

      <div
        class="title title--right lead-1"
        v-text="$t('screen.basket.existingCustomer.tabs.changed')"
      />

      <basket-contract
        class="contract contract--left"
        is-read-only-contract-start
        is-read-only-location
        :contract-data="contractContractData"
      />

      <basket-contract
        class="contract contract--right highlight"
        hide-title
        is-read-only-contract-start
        is-read-only-location
        :contract-data="basketContractData"
      />

      <div class="persons">
        <template
          v-for="person in diffPersons"
          :key="person.personId"
        >
          <!-- NAME -->
          <div class="person-details person-details--left">
            <person-name
              class="lead-1"
              :person="person"
            />
            <basic-price
              :price="personTotal(person.existing)"
              price-class="h3"
              :suffix="$t('product.currency')"
              suffix-class="lead-2"
            />
          </div>
          <div class="person-details person-details--right highlight">
            <basic-price
              :price="personTotal(person.changed)"
              price-class="h3"
              :suffix="$t('product.currency')"
              suffix-class="lead-2"
            />
          </div>

          <!-- BIRTHDATE & GENDER -->
          <div
            v-if="person.existing"
            :key="`person-data-${person.personId}-left`"
            class="person-data"
          >
            <div
              v-if="customerNumber(person)"
              class="person-data__entry"
            >
              <span
                class="title-2"
                v-text="$t('person.customerNumber')"
              />
              <span
                class="body-2"
                v-text="customerNumber(person)"
              />
            </div>
            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.birthdate')"
              />
              <span
                class="body-2"
                v-text="dateOfBirth(person.personData.dateOfBirth)"
              />
            </div>

            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.sex')"
              />
              <span
                class="body-2"
                v-text="$t(gender(person.personData.gender))"
              />
            </div>
          </div>
          <div
            v-else
            :key="`person-missing-${person.personId}-left`"
          >
            <basic-message
              class="no-person__message"
              :content="$t('screen.basket.diff.noPerson')"
            />
          </div>

          <div
            v-if="person.changed"
            :key="`person-data-${person.personId}-right`"
            class="person-data highlight"
          >
            <div
              v-if="customerNumber(person)"
              class="person-data__entry"
            >
              <span
                class="title-2"
                v-text="$t('person.customerNumber')"
              />
              <span
                class="body-2"
                v-text="customerNumber(person)"
              />
            </div>
            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.birthdate')"
              />
              <span
                class="body-2"
                v-text="dateOfBirth(person.personData.dateOfBirth)"
              />
            </div>

            <div class="person-data__entry">
              <span
                class="title-2"
                v-text="$t('person.sex')"
              />
              <span
                class="body-2"
                v-text="$t(gender(person.personData.gender))"
              />
            </div>
          </div>
          <div
            v-else
            :key="`person-missing-${person.personId}-right`"
            class="highlight"
          >
            <basic-message
              class="no-person__message"
              :content="$t('screen.basket.diff.noPerson')"
            />
          </div>

          <!-- CATEGORIES -->
          <template
            v-for="(category, index) in person.diffProducts"
            :key="category.id"
          >
            <div
              v-if="category.existing"
              :key="`${person.personId}-category-${category.id}-left`"
              class="products"
              :class="{ 'products--last': isLastCategory(index, person.diffProducts) }"
            >
              <template
                v-for="product in category.existing"
                :key="product.productId"
              >
                <the-product-card
                  read-only
                  :category-id="category.id"
                  :person="person"
                  :product="product"
                  :upselling="false"
                />
              </template>
            </div>
            <div
              v-else
              :key="`${person.personId}-category-missing-${category.id}-left`"
            >
              <!-- EMPTY PLACEHOLDER -->
            </div>
            <div
              v-if="category.changed"
              :key="`${person.personId}-category-${category.id}-right`"
              class="products highlight"
              :class="{ 'products--last': isLastCategory(index, person.diffProducts) }"
            >
              <template
                v-for="product in category.changed"
                :key="product.productId"
              >
                <the-product-card
                  read-only
                  :category-id="category.id"
                  :person="person"
                  :product="product"
                  :upselling="false"
                />
              </template>
            </div>
            <div
              v-else
              :key="`${person.personId}-category-missing-${category.id}-right`"
              class="highlight"
            >
              <!-- EMPTY PLACEHOLDER -->
            </div>
          </template>
        </template>
      </div>

      <div>
        <div class="total total-left">
          <span
            class="h3"
            v-text="$t('basket.grandTotal')"
          />
          <basic-price
            :price="existingTotal"
            price-class="h3"
            :suffix="$t('product.currency')"
            suffix-class="lead-2"
          />
        </div>
        <div class="total__divider" />
      </div>
      <div>
        <div class="total total-right">
          <span
            class="h3"
            v-text="$t('basket.grandTotal')"
          />
          <basic-price
            :price="changedTotal"
            price-class="h3"
            :suffix="$t('product.currency')"
            suffix-class="lead-2"
          />
        </div>
        <div class="total__divider" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed } from 'vue'

  import { BasketUtil } from '@shared/utils/Basket'
  import { DateTimeHelper } from '@shared/helpers'

  import useBasket from '@shared/hooks/useBasket'
  import useContract from '@shared/hooks/useContract'
  import useDiffBasket from '@shared/hooks/useDiffBasket'

  import BasketContract from '@shared/components/Basket/Content/BasketContract'
  import TheProductCard from '@shared/components/Product/TheProductCard'
  import PersonName from '@shared/components/Person/PersonName'
  import { BasicMessage, BasicPrice } from '@shared/components/Basic'

  // HOOKS
  const { contractData: basketContractData } = useBasket()
  const { contractData: contractContractData } = useContract()
  const { basketPersons, contractPersons, diffPersons } = useDiffBasket()

  // COMPUTED
  const changedTotal = computed(() => {
    return basketPersons.value.reduce((sum, person) => sum + person.total, 0)
  })

  const existingTotal = computed(() => {
    return contractPersons.value.reduce((sum, person) => sum + person.total, 0)
  })

  // METHODS
  function customerNumber(person) {
    return person.contractNumber ? BasketUtil.getCustomerNumberFromContract(person.contractNumber) : null
  }

  function dateOfBirth(date) {
    return DateTimeHelper.formatDate(date)
  }

  function gender(personGender) {
    return `person.gender.${personGender.toLowerCase()}`
  }

  function isLastCategory(index, products) {
    return parseInt(index) === Object.keys(products).length - 1
  }

  function personTotal(person) {
    return person?.total || 0
  }
</script>

<style name="mobile" scoped>
.diff {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-03);
  margin: var(--fixed-spacing-fix-12) 0;
}

.data {
  display: grid;
  grid-template-areas:
    'title_left title_right'
    'contract_left contract_right'
    'persons persons'
    'total_left total_right';
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.title {
  padding: 0 0 0 var(--fixed-spacing-fix-06);
  margin: 0 0 var(--fixed-spacing-fix-04);
}

.title--left {
  grid-area: title_left;
}

.title--right {
  grid-area: title_right;
}

.persons {
  grid-area: persons;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.contract {
  padding: var(--fixed-spacing-fix-06);
}

.contract--left {
  grid-area: contract_left;
}

.contract--right {
  grid-area: contract_right;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.person-details {
  display: flex;
  padding: var(--dotcom-responsive-spacing-res-m) var(--fixed-spacing-fix-06);
  border-top: 1px solid var(--on-surface);
}

.person-details--left {
  justify-content: space-between;
}

.person-details--right {
  justify-content: flex-end;
}

.person-data {
  padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-06) var(--dotcom-responsive-spacing-res-s);
}

.person-data__entry {
  display: flex;
  flex-direction: row;
  column-gap: var(--fixed-spacing-fix-02);
  align-items: center;
}

.no-person__message {
  margin: 0 20px;
}

.products {
  display: flex;
  flex-direction: column;
  row-gap: var(--fixed-spacing-fix-04);
  padding: 0 var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-04);
}

.products--last {
  padding: 0 var(--fixed-spacing-fix-06) var(--dotcom-responsive-spacing-res-m);
}

.total {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: var(--dotcom-responsive-spacing-res-m) var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-06);
}

.total--left {
  grid-area: total_left;
}

.total--right {
  grid-area: total_right;
}

.total__divider {
  height: var(--fixed-spacing-fix-04);
  border-top: 1px solid var(--on-surface);
  border-bottom: 1px solid var(--on-surface);
}

.highlight {
  background-color: var(--surface-ext-a);
}
</style>
